
  import { ComponentValue, Version } from '@/entities/vehicle'
  import { Component, Emit, Prop, Watch } from 'vue-property-decorator'
  import { FilesProcess } from '@/components/forms/view/FilesProcess'
  import { RULES } from '@/components/forms'
  import dayjs from 'dayjs'

@Component
  export default class CreateVersion extends FilesProcess {
  @Prop({ type: Boolean, default: false }) readonly value!: boolean
  @Prop({ type: Array, default: [] }) readonly versions!: Version[]
  @Prop({ type: Object, default: null }) readonly model!: any
  @Prop({ type: Object, default: null }) readonly brand!: any
  @Prop({ type: Number, default: null }) readonly year!: number
  @Prop({ type: String, default: '' }) readonly suggestedVersion!: string

  messageVersion = ''
  fieldRequired = RULES.isRequired
  loading = false
  isFormValid = false
  useCavVersion = false

  form = {
    version: null,
    fuel: null,
    bodyWork: null,
    transmission: null,
    traction: null,
    doors: null,
    cylinder: '',
  }

  fuels: ComponentValue[] = []
  bodyWorks: ComponentValue[] = []
  transmissions: ComponentValue[] = []
  traction: ComponentValue[] = []
  doors: ComponentValue[] = []
  cylinder = null
  power = null

  get versionExistsInList () {
    if (!this.suggestedVersion || !this.versions || !this.versions.length) {
      return false
    }
    return this.versions.some((version: any) =>
      version.name.toLowerCase() === this.suggestedVersion.toLowerCase()
    )
  }

  async mounted () {
    this.fuels = await this.fetchData({
      query: { name: 'find', model: 'ComponentValue' },
      filter: {
        component: {
          name: { _eq: 'Combustible' },
        },
      },
    })

    this.bodyWorks = await this.fetchData({
      query: { name: 'find', model: 'ComponentValue' },
      filter: {
        component: {
          name: { _eq: 'Carrocería' },
        },
      },
    })

    this.transmissions = await this.fetchData({
      query: { name: 'find', model: 'ComponentValue' },
      filter: {
        component: {
          name: { _eq: 'Transmisión' },
        },
      },
    })

    this.traction = await this.fetchData({
      query: { name: 'find', model: 'ComponentValue' },
      filter: {
        _and: [
          { component: { name: { _eq: 'Tracción' } } },
          { value: { _neq: '4x2 y 4x4' } },
        ],
      },
    })

    this.doors = await this.fetchData({
      query: { name: 'find', model: 'ComponentValue' },
      filter: {
        component: {
          name: { _eq: 'Nº de puertas' },
        },
      },
    })

    const cylinder = await this.fetchData({
      query: { name: 'find', model: 'Component' },
      filter: {
        name: { _eq: 'Cilindrada' },
      },
    })

    this.cylinder = cylinder[0].id

    const power = await this.fetchData({
      query: { name: 'find', model: 'Component' },
      filter: {
        name: { _eq: 'Potencia' },
      },
    })
    this.power = power[0].id

    // Verificar si la versión sugerida existe en la lista de versiones
    if (this.suggestedVersion && this.versions && this.versions.length) {
      const versionExists = this.versions.some((version: any) =>
        version.name.toLowerCase() === this.suggestedVersion.toLowerCase()
      )

      // Si la versión sugerida no existe, activar automáticamente el switch
      // y aplicar la versión del CAV al formulario
      if (!versionExists) {
        this.useCavVersion = true
        this.form.version = this.suggestedVersion.toUpperCase()
        this.checkVersion()
      }
    }
  }

  handleCavVersionSwitch (value) {
    if (value) {
      // Si se activa el switch, usar la versión CAV
      this.form.version = this.suggestedVersion ? this.suggestedVersion.toUpperCase() : null
    } else {
      // Si se desactiva, limpiar el campo
      this.form.version = null
    }
    this.checkVersion()
  }

  @Watch('form', { immediate: true, deep: true })
  onFormChange () {
    // Validar el formulario
    this.isFormValid = !!(
      this.form.version &&
      this.form.fuel &&
      this.form.bodyWork &&
      this.form.transmission &&
      this.form.traction &&
      this.form.doors
    )
  }

  @Watch('suggestedVersion', { immediate: true })
  onSuggestedVersionChange (newVal) {
    if (newVal) {
      // Verificar si la versión sugerida existe en la lista de versiones
      const versionExists = this.versions && this.versions.length
        ? this.versions.some((version: any) =>
          version.name.toLowerCase() === newVal.toLowerCase()
        ) : false

      // Si la versión sugerida no existe, activar automáticamente el switch
      if (!versionExists) {
        this.useCavVersion = true
        this.form.version = newVal.toUpperCase()
        this.$nextTick(() => {
          this.checkVersion()
        })
      }
    }
  }

  applySuggestedVersion () {
    this.form.version = this.suggestedVersion
    this.checkVersion()
  }

  @Emit('input')
  cancel () {
    this.resetForm()
    return false
  }

  resetForm () {
    this.form = {
      version: null,
      fuel: null,
      bodyWork: null,
      transmission: null,
      traction: null,
      doors: null,
      cylinder: '',
    }
    this.messageVersion = ''
    this.useCavVersion = false
  }

  async checkVersion () {
    const { version } = this.form

    if (version) {
      const versionExists = this.versions.find((item: any) => item.name.toLowerCase() === version.toLowerCase())
      if (versionExists) {
        this.messageVersion = 'La versión ya existe'
      } else {
        this.messageVersion = ''
      }
    }
  }

  // Método para crear campos de atributos
  createAttributesData (form, attr = []) {
    return [
      ...attr,
      {
        id_component: form.bodyWork.component.id,
        id_component_value: form.bodyWork.id,
        validated: true,
      },
      {
        id_component: form.fuel.component.id,
        id_component_value: form.fuel.id,
        validated: true,
      },
      {
        id_component: form.transmission.component.id,
        id_component_value: form.transmission.id,
        validated: true,
      },
      {
        id_component: form.traction.component.id,
        id_component_value: form.traction.id,
        validated: true,
      },
      {
        id_component: form.doors.component.id,
        id_component_value: form.doors.id,
        validated: true,
      },
    ]
  }

  // Método para crear los campos de la versión
  createVersionFields (form, currentYear, attr) {
    return {
      name: form.version.toUpperCase(),
      id_model: this.model.id,
      years: {
        data: [{
          id_year: currentYear,
          attributes: {
            data: this.createAttributesData(form, attr),
          },
        }],
      },
    }
  }

  // Método para crear o actualizar la versión
  async createVersion (fields) {
    return this.pushData({
      model: 'Version',
      fields,
    })
  }

  // Método para crear o actualizar VersionYear
  async handleVersionYear (version, form, currentYear, attr) {
    let versionYear = version?.years?.[0]

    if (!versionYear) {
      // Si no se creó automáticamente, crear el VersionYear manualmente
      const versionYearFields = {
        id_version: version.id,
        id_year: currentYear,
        attributes: {
          data: this.createAttributesData(form, attr),
        },
      }

      versionYear = await this.pushData({
        model: 'VersionYear',
        fields: versionYearFields,
      })
    }

    return versionYear
  }

  // Método para obtener la información completa de VersionYear
  async getCompleteVersionYear (versionYear) {
    const versionYearComplete = await this.fetchData({
      query: { name: 'findLite', model: 'VersionYear' },
      filter: { id: { _eq: versionYear.id || versionYear } },
      force: true,
    })

    return versionYearComplete?.[0] || versionYear
  }

  // Método para validar campos específicos antes de enviar
  validateFields () {
    // Validar que los campos obligatorios estén presentes
    if (!this.form.version || !this.form.fuel || !this.form.bodyWork ||
        !this.form.transmission || !this.form.traction || !this.form.doors) {
      return false
    }

    // Si hay un valor en cylinder, validar que sea string y no esté vacío
    if (this.form.cylinder !== null && this.form.cylinder !== undefined) {
      // Convertir a string si no lo es ya
      this.form.cylinder = String(this.form.cylinder)
    }

    return true
  }

  async send () {
    if (!this.isFormValid || this.messageVersion) {
      return
    }

    // Validación adicional antes de procesar
    if (!this.validateFields()) {
      return
    }

    this.loading = true
    const { form } = this
    const currentYear = this.year || dayjs().year()

    try {
      // Construir los campos de atributos
      const attr = []
      // Validar que cylinder tenga un valor válido antes de agregarlo
      if (form.cylinder && form.cylinder.trim() !== '') {
        attr.push({
          id_component: this.cylinder,
          value: form.cylinder.trim(),
          validated: true,
        })
      }

      // Crear la versión
      const fields = this.createVersionFields(form, currentYear, attr)
      const version = await this.createVersion(fields)

      // Verificar si se creó correctamente el VersionYear
      const versionYear = await this.handleVersionYear(version, form, currentYear, attr)

      // Obtener la información completa de VersionYear
      const completeVersionYear = await this.getCompleteVersionYear(versionYear)

      // Emitir el evento con la versión creada y la información de VersionYear
      const completeData = { version, versionYear: completeVersionYear }
      this.$emit('created', completeData)

      // Cerrar el diálogo
      this.cancel()
    } catch (error) {
      console.error('Error al crear la versión:', error)
    } finally {
      this.loading = false
    }
  }

  // Método para manejar la entrada de cilindrada
  handleCylinderInput (value) {
    // Si el valor está vacío, asignar string vacío en lugar de null
    if (!value && value !== 0) {
      this.form.cylinder = ''
    }
  }
  }
